import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Form, ProgressBar } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

/**
 * IP Extraction - Command Center Version
 * Guided conversation to extract intellectual property into frameworks
 */
function IPExtraction() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Session state
  const [frameworkId, setFrameworkId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [question, setQuestion] = useState(null);
  const [totalQuestions, setTotalQuestions] = useState(11);
  const [answers, setAnswers] = useState({});
  const [currentAnswer, setCurrentAnswer] = useState('');

  // Completion state
  const [isComplete, setIsComplete] = useState(false);
  const [frameworkName, setFrameworkName] = useState('');
  const [completedFramework, setCompletedFramework] = useState(null);

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editedFramework, setEditedFramework] = useState(null);
  const [saving, setSaving] = useState(false);

  // Enhancement suggestions state
  const [loadingEnhancements, setLoadingEnhancements] = useState(false);
  const [enhancements, setEnhancements] = useState(null);
  const [addedToTasks, setAddedToTasks] = useState({});  // Track which items have been added

  // Existing frameworks
  const [existingFrameworks, setExistingFrameworks] = useState([]);

  useEffect(() => {
    loadExistingFrameworks();
  }, []);

  const loadExistingFrameworks = async () => {
    try {
      const response = await fetch('/api/authority-engineer/extract/frameworks', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setExistingFrameworks(data.frameworks || []);
        }
      }
    } catch (err) {
      console.error('Error loading frameworks:', err);
    } finally {
      setLoading(false);
    }
  };

  const startExtraction = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/authority-engineer/extract/start', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      if (data.success) {
        setFrameworkId(data.framework_id);
        setCurrentQuestion(data.current_question);
        setQuestion(data.question);
        setTotalQuestions(data.total_questions);
        setAnswers(data.answers || {});

        // Pre-fill answer if resuming
        if (data.resuming && data.question) {
          setCurrentAnswer(data.answers?.[data.question.id] || '');
        }
      } else {
        setError(data.error || 'Failed to start extraction');
      }
    } catch (err) {
      setError('Failed to start extraction');
    } finally {
      setLoading(false);
    }
  };

  const submitAnswer = async () => {
    if (!currentAnswer.trim()) {
      setError('Please provide an answer before continuing.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/authority-engineer/extract/answer', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          framework_id: frameworkId,
          question_id: question.id,
          answer: currentAnswer.trim()
        })
      });

      const data = await response.json();
      if (data.success) {
        if (data.complete) {
          // All questions answered
          setAnswers(data.answers);
          setIsComplete(true);
          // Pre-fill framework name from s5_naming answer
          setFrameworkName(data.answers?.s5_naming || '');
        } else {
          // Move to next question
          setCurrentQuestion(data.current_question);
          setQuestion(data.question);
          setCurrentAnswer('');
        }
      } else {
        setError(data.error || 'Failed to submit answer');
      }
    } catch (err) {
      setError('Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  const completeExtraction = async () => {
    if (!frameworkName.trim()) {
      setError('Please name your framework.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/authority-engineer/extract/complete', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          framework_id: frameworkId,
          name: frameworkName.trim()
        })
      });

      const data = await response.json();
      if (data.success) {
        setCompletedFramework(data.framework);
      } else {
        setError(data.error || 'Failed to complete extraction');
      }
    } catch (err) {
      setError('Failed to complete extraction');
    } finally {
      setSubmitting(false);
    }
  };

  const startEditing = () => {
    // Deep copy the framework for editing
    setEditedFramework({
      name: completedFramework.name,
      identity: { ...completedFramework.identity },
      painPatterns: { ...completedFramework.painPatterns },
      decisionLogic: { ...completedFramework.decisionLogic },
      boundaries: { ...completedFramework.boundaries },
      language: { ...completedFramework.language }
    });
    setEditMode(true);
  };

  const cancelEditing = () => {
    setEditMode(false);
    setEditedFramework(null);
  };

  const updateEditField = (section, field, value) => {
    setEditedFramework(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const saveEdits = async () => {
    setSaving(true);
    setError('');

    try {
      const response = await fetch('/api/authority-engineer/extract/update', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          framework_id: completedFramework.id,
          name: editedFramework.name,
          framework_data: {
            identity: editedFramework.identity,
            painPatterns: editedFramework.painPatterns,
            decisionLogic: editedFramework.decisionLogic,
            boundaries: editedFramework.boundaries,
            language: editedFramework.language
          }
        })
      });

      const data = await response.json();
      if (data.success) {
        setCompletedFramework({
          ...completedFramework,
          name: editedFramework.name,
          identity: editedFramework.identity,
          painPatterns: editedFramework.painPatterns,
          decisionLogic: editedFramework.decisionLogic,
          boundaries: editedFramework.boundaries,
          language: editedFramework.language
        });
        setEditMode(false);
        setEditedFramework(null);
      } else {
        setError(data.error || 'Failed to save changes');
      }
    } catch (err) {
      setError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const getEnhancementSuggestions = async () => {
    setLoadingEnhancements(true);
    setError('');

    try {
      const response = await fetch('/api/authority-engineer/extract/enhance', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          framework_id: completedFramework.id
        })
      });

      const data = await response.json();
      if (data.success) {
        setEnhancements(data.suggestions);
      } else {
        setError(data.error || 'Failed to get enhancement suggestions');
      }
    } catch (err) {
      setError('Failed to get enhancement suggestions');
    } finally {
      setLoadingEnhancements(false);
    }
  };

  const addEnhancementToTasks = async (text, category, itemKey) => {
    try {
      const response = await fetch('/api/authority-engineer/extract/add-task', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          framework_id: completedFramework.id,
          enhancement_text: text,
          enhancement_category: category
        })
      });

      const data = await response.json();
      if (data.success) {
        setAddedToTasks(prev => ({ ...prev, [itemKey]: true }));
      } else {
        setError(data.error || 'Failed to add to tasks');
      }
    } catch (err) {
      setError('Failed to add to tasks');
    }
  };

  const viewFramework = async (frameworkId) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`/api/authority-engineer/extract/framework/${frameworkId}`, {
        credentials: 'include'
      });

      const data = await response.json();
      if (data.success) {
        setCompletedFramework(data.framework);
        // Load enhancements if they exist
        if (data.framework.enhancements) {
          setEnhancements(data.framework.enhancements);
        }
      } else {
        setError(data.error || 'Failed to load framework');
      }
    } catch (err) {
      setError('Failed to load framework');
    } finally {
      setLoading(false);
    }
  };

  const progressPercent = totalQuestions > 0 ? Math.round((currentQuestion / totalQuestions) * 100) : 0;

  // Loading state
  if (loading) {
    return (
      <Container fluid className="py-4 px-4">
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-2">Loading...</p>
        </div>
      </Container>
    );
  }

  // Completed - show framework summary
  if (completedFramework) {
    const fw = editMode ? editedFramework : completedFramework;

    return (
      <Container fluid className="py-4 px-4">
        <div className="mb-4">
          <Link to="/command-center/authority-engineer" className="text-decoration-none mb-2 d-inline-block">
            ← Back to Authority Engineer
          </Link>
          <h1>{editMode ? 'Edit Framework' : 'Framework Extracted!'}</h1>
        </div>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

        <Row>
          <Col lg={8}>
            <Card className="shadow mb-4" style={{ border: `2px solid ${editMode ? '#007bff' : '#28a745'}` }}>
              <Card.Header style={{ backgroundColor: editMode ? '#007bff' : '#28a745', color: 'white' }} className="d-flex justify-content-between align-items-center">
                {editMode ? (
                  <Form.Control
                    type="text"
                    value={fw.name}
                    onChange={(e) => setEditedFramework(prev => ({ ...prev, name: e.target.value }))}
                    style={{ maxWidth: '400px', fontWeight: 'bold', fontSize: '1.25rem' }}
                  />
                ) : (
                  <h4 className="mb-0">{fw.name}</h4>
                )}
                {!editMode && (
                  <Button variant="light" size="sm" onClick={startEditing}>
                    Edit
                  </Button>
                )}
              </Card.Header>
              <Card.Body>
                <h5 className="mt-3">Expert Territory</h5>
                {editMode ? (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>What you do:</strong></Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={fw.identity?.broadStatement || ''}
                        onChange={(e) => updateEditField('identity', 'broadStatement', e.target.value)}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Common pattern you see:</strong></Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={fw.identity?.pattern || ''}
                        onChange={(e) => updateEditField('identity', 'pattern', e.target.value)}
                      />
                    </Form.Group>
                    <div className="mb-3">
                      <Form.Label><strong>Domains you synthesize:</strong></Form.Label>
                      {(fw.identity?.domains || []).map((domain, idx) => (
                        <div key={idx} className="mb-2 p-2 bg-light rounded">
                          <Form.Control
                            type="text"
                            value={domain.name || ''}
                            onChange={(e) => {
                              const newDomains = [...(fw.identity?.domains || [])];
                              newDomains[idx] = { ...newDomains[idx], name: e.target.value };
                              updateEditField('identity', 'domains', newDomains);
                            }}
                            className="mb-1 fw-bold"
                            placeholder="Category name"
                          />
                          <Form.Control
                            type="text"
                            value={(domain.includes || []).join(', ')}
                            onChange={(e) => {
                              const newDomains = [...(fw.identity?.domains || [])];
                              newDomains[idx] = { ...newDomains[idx], includes: e.target.value.split(',').map(d => d.trim()).filter(d => d) };
                              updateEditField('identity', 'domains', newDomains);
                            }}
                            placeholder="Sub-areas (comma-separated)"
                          />
                        </div>
                      ))}
                    </div>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>How these domains integrate:</strong></Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={fw.identity?.integrationApproach || ''}
                        onChange={(e) => updateEditField('identity', 'integrationApproach', e.target.value)}
                        placeholder="Describe how all your domains work together (e.g., 'All domains are used in an integrative way, fuel-injected with AI')"
                      />
                    </Form.Group>
                  </>
                ) : (
                  <>
                    <p><strong>What you do:</strong> {fw.identity?.broadStatement}</p>
                    <p><strong>Common pattern you see:</strong> {fw.identity?.pattern}</p>
                    {fw.identity?.domains?.length > 0 && (
                      <div className="mb-3">
                        <strong>Domains you synthesize:</strong>
                        <ul className="mt-2 mb-0">
                          {fw.identity.domains.map((domain, idx) => (
                            <li key={idx}>
                              <strong>{domain.name}</strong>
                              {domain.includes?.length > 0 && (
                                <span className="text-muted"> — {domain.includes.join(', ')}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {fw.identity?.integrationApproach && (
                      <p><strong>How these domains integrate:</strong> {fw.identity.integrationApproach}</p>
                    )}
                  </>
                )}

                <hr />
                <h5>Pain Patterns</h5>
                {editMode ? (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Common mistakes:</strong></Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={fw.painPatterns?.mistakes || ''}
                        onChange={(e) => updateEditField('painPatterns', 'mistakes', e.target.value)}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Mistake sequence:</strong></Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={fw.painPatterns?.mistakeSequence || ''}
                        onChange={(e) => updateEditField('painPatterns', 'mistakeSequence', e.target.value)}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Bad advice:</strong></Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={fw.painPatterns?.failedAdvice || ''}
                        onChange={(e) => updateEditField('painPatterns', 'failedAdvice', e.target.value)}
                      />
                    </Form.Group>
                  </>
                ) : (
                  <>
                    <p><strong>Common mistakes:</strong> {fw.painPatterns?.mistakes}</p>
                    {fw.painPatterns?.mistakeSequence && (
                      <p><strong>Mistake sequence:</strong> {fw.painPatterns.mistakeSequence}</p>
                    )}
                    <p><strong>Bad advice:</strong> {fw.painPatterns?.failedAdvice}</p>
                  </>
                )}

                <hr />
                <h5>Decision Logic</h5>
                {editMode ? (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>How you decide:</strong></Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={fw.decisionLogic?.framework || ''}
                        onChange={(e) => updateEditField('decisionLogic', 'framework', e.target.value)}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>What comes first:</strong></Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={fw.decisionLogic?.sequence || ''}
                        onChange={(e) => updateEditField('decisionLogic', 'sequence', e.target.value)}
                      />
                    </Form.Group>
                  </>
                ) : (
                  <>
                    <p><strong>How you decide:</strong> {fw.decisionLogic?.framework}</p>
                    <p><strong>What comes first:</strong> {fw.decisionLogic?.sequence}</p>
                  </>
                )}

                <hr />
                <h5>Boundaries</h5>
                {editMode ? (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Not for:</strong></Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={fw.boundaries?.notFor || ''}
                        onChange={(e) => updateEditField('boundaries', 'notFor', e.target.value)}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>What you reject:</strong></Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={fw.boundaries?.rejections || ''}
                        onChange={(e) => updateEditField('boundaries', 'rejections', e.target.value)}
                      />
                    </Form.Group>
                  </>
                ) : (
                  <>
                    <p><strong>Not for:</strong> {fw.boundaries?.notFor}</p>
                    <p><strong>What you reject:</strong> {fw.boundaries?.rejections}</p>
                  </>
                )}

                <hr />
                <h5>Your Language & Terminology</h5>
                {editMode ? (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Terms you use:</strong></Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={fw.language?.terms || ''}
                        onChange={(e) => updateEditField('language', 'terms', e.target.value)}
                        placeholder="Comma-separated list of your coined terms"
                      />
                      <Form.Text className="text-muted">List your unique terminology (e.g., Authority Score, Market Domination Matrix)</Form.Text>
                    </Form.Group>

                    <Form.Label><strong>Term Definitions (Glossary):</strong></Form.Label>
                    <div className="mb-3 p-3 bg-light rounded">
                      {(fw.language?.glossary || []).map((item, idx) => (
                        <div key={idx} className="d-flex gap-2 mb-2 align-items-start">
                          <Form.Control
                            type="text"
                            placeholder="Term"
                            value={item.term || ''}
                            onChange={(e) => {
                              const glossary = [...(fw.language?.glossary || [])];
                              glossary[idx] = { ...glossary[idx], term: e.target.value };
                              updateEditField('language', 'glossary', glossary);
                            }}
                            style={{ maxWidth: '200px' }}
                          />
                          <Form.Control
                            type="text"
                            placeholder="Definition - what this term means"
                            value={item.definition || ''}
                            onChange={(e) => {
                              const glossary = [...(fw.language?.glossary || [])];
                              glossary[idx] = { ...glossary[idx], definition: e.target.value };
                              updateEditField('language', 'glossary', glossary);
                            }}
                          />
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => {
                              const glossary = (fw.language?.glossary || []).filter((_, i) => i !== idx);
                              updateEditField('language', 'glossary', glossary);
                            }}
                          >
                            ×
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => {
                          const glossary = [...(fw.language?.glossary || []), { term: '', definition: '' }];
                          updateEditField('language', 'glossary', glossary);
                        }}
                      >
                        + Add Term Definition
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p><strong>Terms you use:</strong> {fw.language?.terms || 'None specified'}</p>
                    {fw.language?.glossary && fw.language.glossary.length > 0 && (
                      <div className="mt-2">
                        <strong>Glossary:</strong>
                        <ul className="mb-0 mt-1">
                          {fw.language.glossary.map((item, idx) => (
                            <li key={idx}><strong>{item.term}</strong>: {item.definition}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}

                {/* Proprietary Assessment */}
                {!editMode && completedFramework.assessment && (
                  <>
                    <hr />
                    <h5>IP Assessment</h5>
                    <div className="d-flex align-items-center mb-2">
                      <strong className="me-2">Proprietary Score:</strong>
                      {[1, 2, 3, 4, 5].map(n => (
                        <span
                          key={n}
                          style={{
                            display: 'inline-block',
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            backgroundColor: n <= completedFramework.assessment.proprietaryScore ? '#28a745' : '#e9ecef',
                            marginRight: '4px'
                          }}
                        />
                      ))}
                      <span className="ms-2 text-muted">({completedFramework.assessment.proprietaryScore}/5)</span>
                    </div>
                    <p className="text-muted">{completedFramework.assessment.proprietaryAssessment}</p>

                    {!enhancements && (
                      <Button
                        variant="outline-primary"
                        onClick={getEnhancementSuggestions}
                        disabled={loadingEnhancements}
                      >
                        {loadingEnhancements ? (
                          <><Spinner size="sm" className="me-2" />Getting Suggestions...</>
                        ) : (
                          'Get Enhancement Suggestions'
                        )}
                      </Button>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>

            {/* Enhancement Suggestions */}
            {enhancements && !editMode && (
              <Card className="shadow mb-4" style={{ border: '2px solid #007bff' }}>
                <Card.Header style={{ backgroundColor: '#007bff', color: 'white' }}>
                  <h5 className="mb-0">Enhancement Suggestions</h5>
                </Card.Header>
                <Card.Body>
                  <p className="text-muted mb-3">
                    These suggestions can help make your IP more distinctive. You may already do some of these —
                    consider adding them to your framework description by clicking the Edit button above.
                    Any new items you want to incorporate into your framework, hit the "+ Task" button on the right
                    to add them to your Task List so you don't forget.
                  </p>

                  {enhancements.uniqueProcesses?.length > 0 && (
                    <div className="mb-3">
                      <h6>Unique Processes You Might Add</h6>
                      <ul className="list-unstyled">
                        {enhancements.uniqueProcesses.map((item, idx) => {
                          const key = `process-${idx}`;
                          return (
                            <li key={idx} className="d-flex align-items-start mb-2">
                              <span className="me-2">•</span>
                              <span className="flex-grow-1">{item}</span>
                              {addedToTasks[key] ? (
                                <span className="text-success ms-2">✓ Added</span>
                              ) : (
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="ms-2"
                                  onClick={() => addEnhancementToTasks(item, 'Unique Process', key)}
                                >
                                  + Task
                                </Button>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {enhancements.proprietaryTerms?.length > 0 && (
                    <div className="mb-3">
                      <h6>Proprietary Terms to Consider</h6>
                      <ul className="list-unstyled">
                        {enhancements.proprietaryTerms.map((item, idx) => {
                          const key = `term-${idx}`;
                          return (
                            <li key={idx} className="d-flex align-items-start mb-2">
                              <span className="me-2">•</span>
                              <span className="flex-grow-1">{item}</span>
                              {addedToTasks[key] ? (
                                <span className="text-success ms-2">✓ Added</span>
                              ) : (
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="ms-2"
                                  onClick={() => addEnhancementToTasks(item, 'Proprietary Term', key)}
                                >
                                  + Task
                                </Button>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {enhancements.differentiators?.length > 0 && (
                    <div className="mb-3">
                      <h6>Ways to Differentiate Further</h6>
                      <ul className="list-unstyled">
                        {enhancements.differentiators.map((item, idx) => {
                          const key = `diff-${idx}`;
                          return (
                            <li key={idx} className="d-flex align-items-start mb-2">
                              <span className="me-2">•</span>
                              <span className="flex-grow-1">{item}</span>
                              {addedToTasks[key] ? (
                                <span className="text-success ms-2">✓ Added</span>
                              ) : (
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="ms-2"
                                  onClick={() => addEnhancementToTasks(item, 'Differentiator', key)}
                                >
                                  + Task
                                </Button>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {enhancements.frameworkElements?.length > 0 && (
                    <div className="mb-3">
                      <h6>Framework Elements to Consider</h6>
                      <ul className="list-unstyled">
                        {enhancements.frameworkElements.map((item, idx) => {
                          const key = `element-${idx}`;
                          return (
                            <li key={idx} className="d-flex align-items-start mb-2">
                              <span className="me-2">•</span>
                              <span className="flex-grow-1">{item}</span>
                              {addedToTasks[key] ? (
                                <span className="text-success ms-2">✓ Added</span>
                              ) : (
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="ms-2"
                                  onClick={() => addEnhancementToTasks(item, 'Framework Element', key)}
                                >
                                  + Task
                                </Button>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {enhancements.storytelling && (
                    <div className="mb-3">
                      <h6>Storytelling Opportunity</h6>
                      <div className="d-flex align-items-start">
                        <p className="flex-grow-1 mb-0">{enhancements.storytelling}</p>
                        {addedToTasks['storytelling'] ? (
                          <span className="text-success ms-2">✓ Added</span>
                        ) : (
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="ms-2"
                            onClick={() => addEnhancementToTasks(enhancements.storytelling, 'Storytelling', 'storytelling')}
                          >
                            + Task
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {enhancements.overallRecommendation && (
                    <div className="p-3 bg-light rounded">
                      <h6>Top Recommendation</h6>
                      <div className="d-flex align-items-start">
                        <p className="mb-0 flex-grow-1">{enhancements.overallRecommendation}</p>
                        {addedToTasks['recommendation'] ? (
                          <span className="text-success ms-2">✓ Added</span>
                        ) : (
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="ms-2"
                            onClick={() => addEnhancementToTasks(enhancements.overallRecommendation, 'Top Recommendation', 'recommendation')}
                          >
                            + Task
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            )}

            <div className="d-flex gap-3">
              {editMode ? (
                <>
                  <Button
                    variant="success"
                    onClick={saveEdits}
                    disabled={saving}
                  >
                    {saving ? <><Spinner size="sm" className="me-2" />Saving...</> : 'Save Changes'}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={cancelEditing}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline-dark"
                    onClick={() => {
                      setCompletedFramework(null);
                      setIsComplete(false);
                      setFrameworkId(null);
                      setQuestion(null);
                      setCurrentQuestion(0);
                      setAnswers({});
                      loadExistingFrameworks();
                    }}
                  >
                    Extract Another Framework
                  </Button>
                  <Button
                    variant="dark"
                    onClick={() => navigate('/command-center/authority-engineer')}
                  >
                    Back to Authority Engineer
                  </Button>
                </>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  // Naming phase - all questions answered
  if (isComplete) {
    return (
      <Container fluid className="py-4 px-4">
        <div className="mb-4">
          <Link to="/command-center/authority-engineer" className="text-decoration-none mb-2 d-inline-block">
            ← Back to Authority Engineer
          </Link>
          <h1>Name Your Framework</h1>
        </div>

        <Row>
          <Col lg={8}>
            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

            <Card className="shadow">
              <Card.Body className="p-4">
                <p className="text-muted mb-4">
                  You've completed the extraction! Now give your methodology a name.
                  This could be what you already call it, or something new.
                </p>

                <Form.Group className="mb-4">
                  <Form.Label><strong>Framework Name</strong></Form.Label>
                  <Form.Control
                    type="text"
                    value={frameworkName}
                    onChange={(e) => setFrameworkName(e.target.value)}
                    placeholder="e.g., The Clarity Method, Strategic Foundation Framework"
                    size="lg"
                  />
                </Form.Group>

                <Button
                  size="lg"
                  style={{ backgroundColor: '#28a745', border: 'none' }}
                  onClick={completeExtraction}
                  disabled={submitting || !frameworkName.trim()}
                >
                  {submitting ? (
                    <><Spinner size="sm" className="me-2" />Saving...</>
                  ) : (
                    'Complete Extraction'
                  )}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Active extraction - showing questions
  if (question) {
    return (
      <Container fluid className="py-4 px-4">
        <div className="mb-4">
          <Link to="/command-center/authority-engineer" className="text-decoration-none mb-2 d-inline-block">
            ← Back to Authority Engineer
          </Link>
          <h1>Extract Your IP</h1>
        </div>

        <Row>
          <Col lg={8}>
            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

            {/* Progress */}
            <Card className="shadow mb-4">
              <Card.Body className="py-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted">
                    Layer {question.layer}: {question.layer_name}
                  </span>
                  <span className="text-muted">
                    Question {currentQuestion + 1} of {totalQuestions}
                  </span>
                </div>
                <ProgressBar now={progressPercent} style={{ height: '8px' }} />
              </Card.Body>
            </Card>

            {/* Question */}
            <Card className="shadow">
              <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                <h5 className="mb-0">Layer {question.layer}: {question.layer_name}</h5>
              </Card.Header>
              <Card.Body className="p-4">
                <p className="lead mb-4">{question.question}</p>

                <Form.Group className="mb-4">
                  <Form.Control
                    as="textarea"
                    rows={5}
                    value={currentAnswer}
                    onChange={(e) => setCurrentAnswer(e.target.value)}
                    placeholder="Type your answer here..."
                    style={{ fontSize: '1.1rem' }}
                  />
                </Form.Group>

                <div className="d-flex justify-content-between">
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/command-center/authority-engineer')}
                    disabled={submitting}
                  >
                    Save & Exit
                  </Button>
                  <Button
                    size="lg"
                    style={{ backgroundColor: colors.greyDarkest, border: 'none' }}
                    onClick={submitAnswer}
                    disabled={submitting || !currentAnswer.trim()}
                  >
                    {submitting ? (
                      <><Spinner size="sm" className="me-2" />Saving...</>
                    ) : (
                      'Continue →'
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Sidebar - Layer guide */}
          <Col lg={4}>
            <Card className="shadow">
              <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                <h6 className="mb-0">The 5 Layers</h6>
              </Card.Header>
              <Card.Body className="p-3">
                {[
                  { num: 1, name: 'Expert Territory', desc: 'What you do & patterns you see' },
                  { num: 2, name: 'Pain Patterns', desc: 'Mistakes people make' },
                  { num: 3, name: 'Decision Logic', desc: 'How you decide what to do' },
                  { num: 4, name: 'Boundaries', desc: 'What you reject' },
                  { num: 5, name: 'Language', desc: 'Your terminology' }
                ].map(layer => (
                  <div
                    key={layer.num}
                    className={`d-flex align-items-start mb-2 p-2 rounded ${question.layer === layer.num ? 'bg-light' : ''}`}
                    style={{ opacity: question.layer >= layer.num ? 1 : 0.5 }}
                  >
                    <span
                      className="me-2 rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: '24px',
                        height: '24px',
                        fontSize: '0.8rem',
                        backgroundColor: question.layer > layer.num ? '#28a745' : (question.layer === layer.num ? colors.greyDarkest : '#dee2e6'),
                        color: question.layer >= layer.num ? 'white' : '#6c757d'
                      }}
                    >
                      {question.layer > layer.num ? '✓' : layer.num}
                    </span>
                    <div>
                      <strong style={{ fontSize: '0.9rem' }}>{layer.name}</strong>
                      <small className="text-muted d-block">{layer.desc}</small>
                    </div>
                  </div>
                ))}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Start screen - no active extraction
  return (
    <Container fluid className="py-4 px-4">
      <div className="mb-4">
        <Link to="/command-center/authority-engineer" className="text-decoration-none mb-2 d-inline-block">
          ← Back to Authority Engineer
        </Link>
        <h1>Extract Your IP</h1>
        <p className="text-muted">Turn the expertise in your head into documented, defensible frameworks</p>
      </div>

      <Row>
        <Col lg={8}>
          {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

          <Card className="shadow mb-4">
            <Card.Body className="p-4">
              <h4 className="mb-3">How This Works</h4>
              <p>
                Through a series of guided questions, we'll extract ONE piece of intellectual property
                from your expertise. The process takes about 15-20 minutes.
              </p>

              <h5 className="mt-4 mb-3">The 5-Layer Extraction:</h5>
              <ol className="mb-4">
                <li className="mb-2"><strong>Expert Territory</strong> - What you do and the patterns you see</li>
                <li className="mb-2"><strong>Pain Patterns</strong> - The mistakes people make before finding you</li>
                <li className="mb-2"><strong>Decision Logic</strong> - How you decide what approach to take</li>
                <li className="mb-2"><strong>Boundaries</strong> - What you reject and who it's not for</li>
                <li className="mb-2"><strong>Language</strong> - Your unique terminology and framework name</li>
              </ol>

              <Button
                size="lg"
                style={{ backgroundColor: colors.greyDarkest, border: 'none' }}
                onClick={startExtraction}
              >
                Start IP Extraction →
              </Button>
            </Card.Body>
          </Card>

          {/* Existing Frameworks */}
          {existingFrameworks.length > 0 && (
            <Card className="shadow">
              <Card.Header>
                <h5 className="mb-0">Your Extracted Frameworks</h5>
              </Card.Header>
              <Card.Body>
                {existingFrameworks.map(fw => (
                  <div key={fw.id} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                    <div>
                      <strong>{fw.name}</strong>
                      <small className="text-muted d-block">
                        {fw.status === 'extracting' ? 'In Progress' : fw.status === 'draft' ? 'Complete' : fw.status}
                        {fw.created_at && ` • Created ${new Date(fw.created_at).toLocaleDateString()}`}
                      </small>
                    </div>
                    <div className="d-flex gap-2">
                      {fw.status === 'extracting' ? (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={startExtraction}
                        >
                          Continue
                        </Button>
                      ) : (
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => viewFramework(fw.id)}
                        >
                          View
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
}

export default IPExtraction;
